import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, Smartphone, X, Share, PlusSquare, CheckCircle2 } from "lucide-react";
import { btnPrimary, btnSecondary } from "./crm";
import { currentSubscription, disablePush, enablePush, needsHomeScreenInstall, permissionState, pushSupported, registerServiceWorker } from "./push";
import { errMsg } from "./useCrm";

/** Tracks whether THIS device is subscribed to lead alerts for the signed-in user. */
export function usePush(api, enabled = true) {
  const [state, setState] = useState({ supported: pushSupported(), subscribed: false, permission: permissionState(), needsInstall: needsHomeScreenInstall(), ready: false });

  const refresh = useCallback(async () => {
    const sub = await currentSubscription();
    setState((s) => ({ ...s, subscribed: !!sub, permission: permissionState(), ready: true }));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    registerServiceWorker().then(refresh);
  }, [enabled, refresh]);

  const enable = async () => {
    await enablePush(api);
    await refresh();
  };
  const disable = async () => {
    await disablePush(api);
    await refresh();
  };
  const sendTest = async () => {
    await api.post("/push/test");
  };
  return { ...state, enable, disable, sendTest, refresh };
}

function Row({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-600"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="mt-0.5 text-xs text-slate-500">{children}</div>
      </div>
    </div>
  );
}

export default function NotificationsSheet({ push, onClose, role }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e?.response ? errMsg(e, "Something went wrong.") : e.message); }
    finally { setBusy(false); }
  };
  const what = role === "agent" ? "the moment a recruit is routed or reassigned to you" : "whenever a new recruit applies or is reassigned";

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/40 sm:px-4" onClick={onClose} data-testid="notifications-sheet">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} className="crm-root w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-slate-200 bg-white p-6 pb-safe shadow-2xl" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
        <div className="flex items-start justify-between">
          <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${push.subscribed ? "bg-emerald-50 text-emerald-600" : "bg-[#0b1528] text-[#00C6EF]"}`}>{push.subscribed ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}</span>
          <button onClick={onClose} data-testid="notifications-close" className="flex h-10 w-10 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <h2 className="mt-4 text-lg font-extrabold text-slate-900">Phone notifications</h2>
        <p className="mt-1 text-sm text-slate-500">Get a notification on this device {what}.</p>

        <div className="mt-5 space-y-2.5">
          {!push.supported && <Row icon={BellOff} title="Not supported in this browser">Use Chrome, Edge, Safari 16.4+ or the installed Home Screen app.</Row>}
          {push.supported && push.needsInstall && (
            <Row icon={Smartphone} title="Add Breeze to your Home Screen first">
              iPhone only delivers notifications to installed web apps. In Safari tap <Share className="inline h-3.5 w-3.5 -mt-0.5" /> <b>Share</b> → <PlusSquare className="inline h-3.5 w-3.5 -mt-0.5" /> <b>Add to Home Screen</b>, then open Breeze from your Home Screen and come back here.
            </Row>
          )}
          {push.supported && !push.needsInstall && push.permission === "denied" && (
            <Row icon={BellOff} title="Notifications are blocked">Allow notifications for this site in your browser or phone settings, then try again.</Row>
          )}
          {push.subscribed && <Row icon={CheckCircle2} title="This device is receiving alerts">You can enable alerts on more than one phone or computer — each device opts in separately.</Row>}
        </div>

        <div className="mt-5 flex flex-col gap-2">
          {push.subscribed ? (
            <>
              <button disabled={busy} onClick={() => run(push.sendTest, "Test notification sent")} data-testid="notifications-test" className={`${btnPrimary} w-full py-3`}><BellRing className="h-4 w-4" /> Send a test notification</button>
              <button disabled={busy} onClick={() => run(push.disable, "Notifications turned off on this device")} data-testid="notifications-disable" className={`${btnSecondary} w-full`}>Turn off on this device</button>
            </>
          ) : (
            <button disabled={busy || !push.supported || push.needsInstall || push.permission === "denied"} onClick={() => run(push.enable, "Notifications enabled on this device")} data-testid="notifications-enable" className={`${btnPrimary} w-full py-3`}><Bell className="h-4 w-4" /> {busy ? "Enabling…" : "Enable notifications"}</button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/** Slim dashboard banner nudging the user to opt in (dismissible per device). */
export function PushBanner({ push, onOpen }) {
  const key = "breeze_push_banner_dismissed";
  const [hidden, setHidden] = useState(() => localStorage.getItem(key) === "1");
  if (hidden || !push.ready || push.subscribed || !push.supported || push.permission === "denied") return null;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3" data-testid="push-banner">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-sky-700 border border-sky-200"><Bell className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">Turn on phone notifications</div>
        <div className="truncate text-xs text-slate-600">{push.needsInstall ? "Add Breeze to your Home Screen to get instant recruit alerts." : "Know the second a recruit is routed to you."}</div>
      </div>
      <button onClick={onOpen} data-testid="push-banner-open" className="shrink-0 rounded-lg bg-[#0b1528] px-3 py-2 text-xs font-semibold text-white">Set up</button>
      <button onClick={() => { localStorage.setItem(key, "1"); setHidden(true); }} data-testid="push-banner-dismiss" className="shrink-0 rounded-lg p-2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
    </div>
  );
}
